import {
  Button,
  Input,
  Select,
  message,
  Popconfirm,
  Table,
  Tag,
  Typography,
} from "antd";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../nestserver";
import { numberInputGuard } from "../../../../components/inputs/numberInputGuard";
import {
  GRADE_OPTIONS,
  REMARK_OPTIONS,
} from "../../../../helper/assessmentOptions";

export default function AssessmentMarksTable({
  assessmentId,
  subjectId,
  assessmentData,
}) {
  const queryClient = useQueryClient();
  const [dataSource, setDataSource] = useState([]);
  const [changes, setChanges] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [notifying, setNotifying] = useState(false);

  async function getStudents() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/students/subject/" + subjectId,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return res.data;
  }

  const students = useQuery(["students", subjectId], getStudents, {
    enabled: !!subjectId,
  });

  useEffect(() => {
    if (students.isSuccess && students.data) {
      const mergedData = students.data.map((student) => {
        const record = assessmentData?.students?.find(
          (r) => r.student?._id === student._id,
        );
        return {
          key: student._id,
          student: student,
          // The marks row's own id — this is what /assessment/notify takes.
          marksRowId: record?._id,
          status: record?.status,
          notified_at: record?.notified_at,
          marks_obtained: record?.marks_obtained,
          remarks: record?.remarks,
          grade: record?.grade,
          total_marks:
            record?.total_marks || assessmentData?.exam?.total_marks,
        };
      });
      setDataSource(mergedData);
      setChanges({});
      setSelectedRowIds([]);
    }
  }, [students.data, students.isSuccess, assessmentData]);

  const handleChange = (studentId, field, value) => {
    setChanges((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value,
      },
    }));
  };

  const studentNameOf = (record) =>
    record?.student
      ? `${record.student.first_name ?? ""} ${
          record.student.last_name ?? ""
        }`.trim() || "this student"
      : "this student";

  // Pulls the reason out of a Nest exception body ({ message } | { message: [] }).
  const serverReasonOf = (error) => {
    const reason = error?.response?.data?.message;
    return Array.isArray(reason) ? reason[0] : reason;
  };

  const handleSave = async () => {
    if (Object.keys(changes).length === 0) {
      setIsEditing(false);
      return;
    }

    // Validate every edited row up front so a bad value never reaches the
    // server and can't leave some students saved and others not.
    const pending = [];
    for (const studentId of Object.keys(changes)) {
      const change = changes[studentId];
      if (!change) continue;

      const currentRecord = dataSource.find((r) => r.key === studentId);
      const studentName = studentNameOf(currentRecord);
      // The server checks against the assessment's current total.
      const rowMax = Number(
        assessmentData?.exam?.total_marks ??
          currentRecord?.total_marks ??
          0,
      );

      const rawMarks =
        change.marks_obtained !== undefined
          ? String(change.marks_obtained).trim()
          : String(currentRecord?.marks_obtained ?? "");
      const marks = rawMarks === "" ? 0 : Number(rawMarks);

      if (!Number.isFinite(marks) || marks < 0) {
        message.error(`Marks for ${studentName} must be a number of 0 or more.`);
        return;
      }
      if (rowMax > 0 && marks > rowMax) {
        message.error(
          `Marks for ${studentName} cannot be more than the total of ${rowMax}.`,
        );
        return;
      }

      pending.push({
        studentId,
        studentName,
        payload: {
          examId: assessmentId,
          studentId: studentId,
          marks_obtained: marks,
          remarks:
            change.remarks !== undefined
              ? change.remarks
              : currentRecord?.remarks || "",
          grade:
            change.grade !== undefined
              ? change.grade
              : currentRecord?.grade || "",
        },
      });
    }

    if (pending.length === 0) {
      setIsEditing(false);
      return;
    }

    setLoading(true);
    const results = await Promise.allSettled(
      pending.map((item) =>
        axios({
          method: "put",
          url: serverUrl + "/exam-v2/marks",
          data: item.payload,
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        }),
      ),
    );
    setLoading(false);

    const failed = results
      .map((result, index) => ({ result, item: pending[index] }))
      .filter(({ result }) => result.status === "rejected");

    // Whatever did land should be reflected in the table either way.
    queryClient.invalidateQueries(["assessments", assessmentId]);

    if (failed.length === 0) {
      message.success("Marks updated successfully");
      setChanges({});
      setIsEditing(false);
      return;
    }

    const reason = serverReasonOf(failed[0].result.reason);
    message.error(
      failed.length === 1
        ? `Could not save marks for ${failed[0].item.studentName}${
            reason ? `: ${reason}` : ". Please try again."
          }`
        : `Could not save marks for ${failed.length} students${
            reason ? `: ${reason}` : ". Please try again."
          }`,
    );

    // Keep only the failures pending so a retry re-sends just those rows.
    setChanges((prev) => {
      const remaining = {};
      failed.forEach(({ item }) => {
        if (prev[item.studentId]) remaining[item.studentId] = prev[item.studentId];
      });
      return remaining;
    });
  };

  // Only saved rows that still have unshared marks can be notified.
  const isNotifiable = (record) =>
    !!record.marksRowId &&
    record.marks_obtained !== undefined &&
    record.marks_obtained !== null &&
    record.status !== "notified" &&
    record.status !== "acknowledged";

  const handleNotify = async () => {
    if (selectedRowIds.length === 0) return;
    setNotifying(true);
    try {
      const res = await axios({
        method: "patch",
        url: serverUrl + "/exam-v2/notify",
        data: { student_exam_ids: selectedRowIds },
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      message.success(res?.data?.message || "Marks shared with families");
      setSelectedRowIds([]);
      queryClient.invalidateQueries(["assessments", assessmentId]);
    } catch (e) {
      const reason = serverReasonOf(e);
      message.error(reason || "Could not notify. Please try again.");
    } finally {
      setNotifying(false);
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedRowIds
      .map((id) => dataSource.find((r) => r.marksRowId === id)?.key)
      .filter(Boolean),
    onChange: (keys) => {
      setSelectedRowIds(
        keys
          .map((key) => dataSource.find((r) => r.key === key)?.marksRowId)
          .filter(Boolean),
      );
    },
    getCheckboxProps: (record) => ({
      disabled: !isNotifiable(record),
      name: record.student?.first_name,
    }),
  };

  const columns = [
    {
      title: "Student Name",
      dataIndex: ["student", "first_name"],
      key: "_id",
      render: (text, record) => (
        <div className="flex flex-col">
          <span
            style={{ color: "#00627B" }}
            className="text-left text-sm font-medium capitalize"
          >
            {record.student?.first_name} {record.student?.last_name}
          </span>
        </div>
      ),
    },
    {
      title: "Marks Obtained",
      key: "marks_obtained",
      render: (text, record) => {
        const val =
          changes[record.key]?.marks_obtained !== undefined
            ? changes[record.key]?.marks_obtained
            : record.marks_obtained;
        const rowMax = Number(
          assessmentData?.exam?.total_marks ?? record.total_marks ?? 0,
        );
        const entered = val === "" || val === undefined ? null : Number(val);
        const isInvalid =
          entered !== null &&
          (!Number.isFinite(entered) ||
            entered < 0 ||
            (rowMax > 0 && entered > rowMax));
        return (
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              min={0}
              max={rowMax || undefined}
              value={val}
              disabled={!isEditing}
              placeholder="0"
              status={isInvalid ? "error" : undefined}
              title={
                isInvalid ? `Enter a value between 0 and ${rowMax}` : undefined
              }
              style={{ width: 100 }}
              onChange={(e) =>
                handleChange(record.key, "marks_obtained", e.target.value)
              }
              {...numberInputGuard}
            />
            <span className={isInvalid ? "text-red-500" : "text-gray-500"}>
              / {assessmentData?.exam?.total_marks || record.total_marks}
            </span>
          </div>
        );
      },
      sorter: (a, b) =>
        (a.marks_obtained || 0) > (b.marks_obtained || 0) ? 1 : -1,
    },
    {
      // Graded here so the report card can pull it, the same way it already
      // pulls remarks — rather than re-picking a grade per subject per student.
      title: "Grade",
      key: "grade",
      render: (text, record) => {
        const val =
          changes[record.key]?.grade !== undefined
            ? changes[record.key]?.grade
            : record.grade;
        return (
          <Select
            value={val || undefined}
            disabled={!isEditing}
            placeholder="Grade"
            style={{ width: "100%", minWidth: 110 }}
            allowClear
            getPopupContainer={(trigger) => trigger.parentNode}
            onChange={(value) => handleChange(record.key, "grade", value ?? "")}
            options={GRADE_OPTIONS.map((g) => ({ label: g, value: g }))}
          />
        );
      },
    },
    {
      title: "Remarks",
      key: "remarks",
      render: (text, record) => {
        const val =
          changes[record.key]?.remarks !== undefined
            ? changes[record.key]?.remarks
            : record.remarks;
        return (
          <Select
            value={val || undefined}
            disabled={!isEditing}
            placeholder="Remarks"
            style={{ width: "100%", minWidth: 180 }}
            allowClear
            getPopupContainer={(trigger) => trigger.parentNode}
            onChange={(value) =>
              handleChange(record.key, "remarks", value ?? "")
            }
            options={REMARK_OPTIONS.map((r) => ({ label: r, value: r }))}
          />
        );
      },
    },
    {
      title: "Shared with family",
      key: "status",
      render: (text, record) => {
        if (record.status === "acknowledged") {
          return <Tag color="green">Acknowledged</Tag>;
        }
        if (record.status === "notified") {
          return (
            <div className="flex flex-col">
              <Tag color="blue" className="w-fit">
                Notified
              </Tag>
              {record.notified_at && (
                <span className="text-xs text-gray-500 pt-1">
                  {moment(record.notified_at).format("DD MMM YYYY, hh:mm A")}
                </span>
              )}
            </div>
          );
        }
        return <span className="text-gray-400">--</span>;
      },
    },
  ];

  return (
    <div className="py-10">
      <div className="flex justify-between items-center mb-4">
        <div>
          <Typography.Title level={4} style={{ color: "#00627B", margin: 0 }}>
            Student Marks
          </Typography.Title>
        </div>
        <div className="flex space-x-2 p-4">
          <Popconfirm
            title={
              selectedRowIds.length === 1
                ? "Share these marks with the student and their parents?"
                : `Share marks with ${selectedRowIds.length} students and their parents?`
            }
            okText="Notify"
            cancelText="Cancel"
            onConfirm={handleNotify}
            disabled={selectedRowIds.length === 0 || isEditing}
          >
            <Button
              loading={notifying}
              disabled={selectedRowIds.length === 0 || isEditing}
            >
              Notify Parents
              {selectedRowIds.length > 0 ? ` (${selectedRowIds.length})` : ""}
            </Button>
          </Popconfirm>
          <Button
            type="primary"
            onClick={isEditing ? handleSave : () => setIsEditing(true)}
            loading={loading}
          >
            {isEditing ? "Save Marks" : "Edit Marks"}
          </Button>
        </div>
      </div>
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className=" bg-white drop-shadow-md w-full "
      >
        <div>
          <Table
            loading={students.isLoading}
            columns={columns}
            dataSource={dataSource}
            rowSelection={rowSelection}
            pagination={false}
            locale={{
              // Mandatory subjects are auto-assigned to every student, but
              // languages and co-curriculars are opt-in per student — so an
              // empty list here almost always means nobody is enrolled in this
              // subject, not that something is broken.
              emptyText: (
                <div className="py-6 text-center">
                  <div className="text-base text-gray-600">
                    No students are enrolled in this subject
                  </div>
                  <div className="pt-1 text-sm text-gray-400">
                    Assign it to students under Students &rarr; Settings &rarr;
                    Subjects, then reopen this page.
                  </div>
                </div>
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
